import React, { useEffect, useState } from 'react';
import { ModalProps } from '@features/Modal';
import './PreferencesViewModal.scss';
import { Autocomplete, Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { BiSave } from 'react-icons/bi';
import { BinRed, EditPencil } from '@pages/LoginWizard/icons';
import ToggleButton from '../InvestmentPortfolio/ToggleButton';
import InvestmentPortfolio from '../InvestmentPortfolio/InvestmentPortfolio';
import { getSecuritiesAll } from '@pages/Preferences/api';
import { ReactComponent as BarsIcon } from '../../../../shared/assets/svgs/BarsIcon.svg';

interface AuditsViewModalProps extends ModalProps {
    list: any;
    setList: React.Dispatch<React.SetStateAction<any>>;
}

export const AuditsViewModal: React.FC<AuditsViewModalProps> = (props) => {
    const { list, setList, onClose } = props;
    const [listNew, setListNew] = useState<any[]>([]);
    const [securities, setSecurities] = useState<any[]>();
    const [selectedItem, setSelectedItem] = useState<any | null>(null);

    const [percentage, setPercentage] = useState<number | string>('');
    const [cashPercentage, setCashPercentage] = useState<number | string>('');
    const [isPreserved, setIsPreserved] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');

    const [totalPercentage, setTotalPercentage] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        const cashItem = list.find((item: any) => item.symbol === 'Cash');
        const nonCashItems = list.filter((item: any) => item.symbol !== 'Cash');
        const total = nonCashItems.reduce((sum: any, item: any) => sum + Number(item.allocationPercent), 0);

        setListNew(nonCashItems);
        setCashPercentage(cashItem?.allocationPercent || '');
        setTotalPercentage(total);
    }, [list]);

    const handleUpdate = () => {
        const updatedList = [
            ...listNew.filter((item) => item.symbol !== 'Cash'),
            ...(cashPercentage
                ? [
                      {
                          symbol: 'Cash',
                          allocationPercent: Number(cashPercentage),
                          preserved: false,
                      },
                  ]
                : []),
        ];

        setList(updatedList);
        if (onClose) onClose();
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchText.length >= 2) {
                getSecuritiesAsync(searchText);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchText]);

    const getSecuritiesAsync = async (search: string) => {
        try {
            let req = await getSecuritiesAll(search);
            setSecurities(req);
        } catch (error) {}
    };

    useEffect(() => {
        getSecuritiesAsync('');
    }, []);

    const handleAdd = () => {
        if (selectedItem?.symbol && percentage) {
            const symbol = selectedItem.symbol;
            const newPercentage = Number(percentage);

            const currentTotal = listNew.reduce((sum, item) => sum + Number(item.allocationPercent), 0);
            const newTotal = currentTotal + newPercentage + Number(cashPercentage || 0);

            if (newTotal > 100) {
                setErrorMessage('Total percentage cannot exceed 100%');
                return;
            }

            setErrorMessage('');

            setListNew((prevList: any) => {
                const existingItemIndex = prevList.findIndex((item: any) => item.symbol === symbol);

                if (existingItemIndex !== -1) {
                    const updatedList = [...prevList];
                    updatedList[existingItemIndex].allocationPercent += newPercentage;
                    return updatedList;
                } else {
                    return [
                        ...prevList,
                        {
                            symbol,
                            allocationPercent: newPercentage,
                            preserved: isPreserved,
                        },
                    ];
                }
            });

            setTotalPercentage(currentTotal + newPercentage);
            setSelectedItem(null);
            setPercentage('');
        } else {
            if (!selectedItem?.symbol) {
                setErrorMessage(`Please select a security`);
            } else {
                setErrorMessage('Please enter percentage.');
            }
        }
    };

    const handleEdit = (index: number, currentPercentage: number) => {
        setEditIndex(index);
        setEditValue(currentPercentage.toString());
    };

    const handleSave = (index: number) => {
        const updatedPercentage = parseFloat(editValue) || 0;
        const currentItem = listNew[index];

        const newTotal =
            listNew.reduce((sum, item, i) => {
                if (i === index) return sum + updatedPercentage;
                return sum + Number(item.allocationPercent);
            }, 0) + Number(cashPercentage || 0);

        if (newTotal > 100) {
            setErrorMessage('Total percentage cannot exceed 100%');
            return;
        }

        setErrorMessage('');
        const updatedItems = [...listNew];
        updatedItems[index].allocationPercent = updatedPercentage;
        setListNew(updatedItems);
        setTotalPercentage(newTotal - Number(currentItem.allocationPercent) + updatedPercentage);
        setEditIndex(null);
    };

    const handleRemove = (index: number) => {
        setListNew((prevList: any) => {
            const removed = prevList[index];
            const newList = prevList.filter((_: any, i: any) => i !== index);
            setTotalPercentage((prev) => prev - Number(removed.allocationPercent));
            return newList;
        });
    };

    const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseFloat(e.target.value) || 0;
        const totalWithoutCash = listNew.reduce((sum, item) => sum + Number(item.allocationPercent), 0);
        const newTotal = totalWithoutCash + val;

        if (newTotal > 100) {
            setErrorMessage('Total percentage cannot exceed 100%');
        } else {
            setErrorMessage('');
            setCashPercentage(val);
            setTotalPercentage(totalWithoutCash);
        }
    };

    return (
        <div className='widget-preference-table-modal'>
            <div className='widget-preference-table-modal__header'>
                <BarsIcon />
                <div>
                    <div className='widget-preference-table-modal__title'>Create Investment Allocation Model</div>
                </div>
            </div>

            <div className='widget-preference-table-modal__content'>
                <Box>
                    <Box
                        display='flex'
                        flexDirection='column'
                        gap={2}
                    >
                        {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
                        <div className='choose-list-header-update'>
                            Select Security
                            <ToggleButton
                                initialState={isPreserved}
                                onChange={setIsPreserved}
                                label='Preserved'
                            />
                        </div>
                        <div className='security-percentage-div'>
                            <Autocomplete
                                options={securities || []}
                                value={selectedItem}
                                onChange={(event, newValue) => setSelectedItem(newValue)}
                                inputValue={searchText}
                                onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
                                getOptionLabel={(option) => option?.symbol || ''}
                                isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Select a Security'
                                        variant='outlined'
                                        fullWidth
                                    />
                                )}
                                disableClearable
                                className='dropdown-security-percentage-div'
                            />

                            <TextField
                                fullWidth
                                type='number'
                                value={percentage}
                                onChange={(e) => setPercentage(e.target.value)}
                                placeholder='Enter % here'
                                variant='outlined'
                                className='input-profit'
                                margin='normal'
                                InputProps={{
                                    startAdornment: <InputAdornment position='start'>%</InputAdornment>,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:after': {
                                            borderRight: '2px solid #ccc',
                                        },
                                        '.MuiInputBase-inputAdornedStart': {
                                            padding: '9.5px 14px',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        borderLeft: '1px solid #ccc',
                                        background: 'white',
                                        paddingLeft: '10px',
                                    },
                                }}
                            />
                        </div>

                        <div className='choose-list-header-update'>Cash</div>
                        <TextField
                            fullWidth
                            type='number'
                            value={cashPercentage}
                            onChange={handleCashChange}
                            placeholder='Enter % here'
                            variant='outlined'
                            className='input-profit'
                            margin='normal'
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>%</InputAdornment>,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:after': {
                                        borderRight: '2px solid #ccc',
                                    },
                                    '.MuiInputBase-inputAdornedStart': {
                                        padding: '9.5px 14px',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    borderLeft: '1px solid #ccc',
                                    background: 'white',
                                    paddingLeft: '10px',
                                },
                            }}
                        />

                        <Button
                            variant='contained'
                            onClick={handleAdd}
                            className='add-security-percentage-button'
                        >
                            Add
                        </Button>
                    </Box>

                    <Box mt={2}>
                        {listNew.length > 0 && (
                            <>
                                <div className='allocations-list-header'>Allocations list</div>
                                <ul className='list-row-allocations'>
                                    {listNew.map((item: any, index: any) => (
                                        <li
                                            key={index}
                                            className='list-row-entry-alloctions'
                                        >
                                            {item.symbol} {item.preserved ? '(Preserved)' : ''}
                                            {editIndex === index ? (
                                                <TextField
                                                    size='small'
                                                    value={editValue}
                                                    onChange={(e) => setEditValue(e.target.value)}
                                                    className='small-text-field-percentage'
                                                />
                                            ) : (
                                                <span className='margin-left-auto'>{`${item.allocationPercent}%`}</span>
                                            )}
                                            {editIndex === index ? (
                                                <IconButton
                                                    size='small'
                                                    color='primary'
                                                    onClick={() => handleSave(index)}
                                                >
                                                    <BiSave />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    size='small'
                                                    color='secondary'
                                                    onClick={() => handleEdit(index, item.allocationPercent)}
                                                >
                                                    <EditPencil />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size='small'
                                                color='secondary'
                                                onClick={() => handleRemove(index)}
                                            >
                                                <BinRed />
                                            </IconButton>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </Box>
                </Box>
                <div className='right-chart-div'>
                    <InvestmentPortfolio
                        investments={[
                            ...listNew,
                            ...(cashPercentage
                                ? [{ symbol: 'Cash', allocationPercent: Number(cashPercentage), preserved: false }]
                                : []),
                        ]}
                        fromUpdate={true}
                    />
                    <Button
                        variant='contained'
                        onClick={handleUpdate}
                        className='security-update-button'
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};
